import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTrialLimit } from "@/hooks/useTrialLimit";
import { useUserTier } from "@/hooks/useUserTier";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, Lock, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import AppHeader from "@/components/AppHeader";
import AppFooter from "@/components/AppFooter";
import ScanSection from "@/components/invoice/ScanSection";
import SellerForm from "@/components/invoice/SellerForm";
import BuyerForm from "@/components/invoice/BuyerForm";
import LineItemsEditor from "@/components/invoice/LineItemsEditor";
import type { LineItem, BuyerInfo } from "@/components/invoice/types";
import { uploadInvoiceFiles } from "@/lib/uploadInvoiceFiles";

const AUTOSAVE_KEY = "ez-factura-new-invoice-draft";

const NewInvoice = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { hasReachedLimit, remaining, limit, isLoading: limitLoading } = useTrialLimit();
  const { tierSlug } = useUserTier();
  const canScanOCR = tierSlug === "esential" || tierSlug === "profesional";
  const [saving, setSaving] = useState(false);
  const [showAnafWarning, setShowAnafWarning] = useState(false);
  const [cuiLoading, setCuiLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [buyerVatRegistered, setBuyerVatRegistered] = useState<boolean | null>(null);
  const [buyerEFactura, setBuyerEFactura] = useState<boolean | null>(null);
  const [defaultVatRate, setDefaultVatRate] = useState(0);
  const [defaultUnitCode, setDefaultUnitCode] = useState("C62");
  const profileLoaded = useRef(false);

  const savedDraft = useRef(() => {
    try {
      const raw = localStorage.getItem(AUTOSAVE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const draft = savedDraft.current();

  const [buyer, setBuyer] = useState<BuyerInfo>(draft?.buyer ?? { name: "", cui: "", address: "" });
  const [sellerName, setSellerName] = useState(draft?.sellerName ?? "");
  const [sellerCui, setSellerCui] = useState(draft?.sellerCui ?? "");
  const [sellerAddress, setSellerAddress] = useState(draft?.sellerAddress ?? "");
  const [sellerCity, setSellerCity] = useState(draft?.sellerCity ?? "");
  const [sellerCounty, setSellerCounty] = useState(draft?.sellerCounty ?? "");
  const [buyerCity, setBuyerCity] = useState(draft?.buyerCity ?? "");
  const [buyerCounty, setBuyerCounty] = useState(draft?.buyerCounty ?? "");
  const [invoiceNumber, setInvoiceNumber] = useState(draft?.invoiceNumber ?? "");
  const [issueDate, setIssueDate] = useState(draft?.issueDate ?? new Date().toISOString().split("T")[0]);
  const [dueDate, setDueDate] = useState(draft?.dueDate ?? "");
  const [items, setItems] = useState<LineItem[]>(
    draft?.items ?? [{ id: crypto.randomUUID(), item_name: "", quantity: 1, unit_price: 0, vat_rate: 0, unit_code: "C62" }]
  );

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(AUTOSAVE_KEY, JSON.stringify({
          buyer, sellerName, sellerCui, sellerAddress, sellerCity, sellerCounty,
          buyerCity, buyerCounty, invoiceNumber, issueDate, dueDate, items,
        }));
      } catch { }
    }, 500);
    return () => clearTimeout(timer);
  }, [buyer, sellerName, sellerCui, sellerAddress, sellerCity, sellerCounty, buyerCity, buyerCounty, invoiceNumber, issueDate, dueDate, items]);

  const clearDraft = useCallback(() => {
    try { localStorage.removeItem(AUTOSAVE_KEY); } catch { }
  }, []);

  const hasUserInput = useCallback(() => {
    const hasBuyerData = buyer.name.trim() !== "" || buyer.cui.trim() !== "" || buyer.address.trim() !== "";
    const hasBuyerLocation = buyerCity.trim() !== "" || buyerCounty.trim() !== "";
    const hasInvoiceNum = invoiceNumber.trim() !== "";
    const hasDueDateSet = dueDate.trim() !== "";
    const hasItemData = items.some(i => i.item_name.trim() !== "" || i.unit_price > 0);
    return hasBuyerData || hasBuyerLocation || hasInvoiceNum || hasDueDateSet || hasItemData;
  }, [buyer, buyerCity, buyerCounty, invoiceNumber, dueDate, items]);

  const formDataRef = useRef({
    buyer, sellerName, sellerCui, sellerAddress, sellerCity, sellerCounty,
    buyerCity, buyerCounty, invoiceNumber, issueDate, dueDate, items,
  });
  useEffect(() => {
    formDataRef.current = {
      buyer, sellerName, sellerCui, sellerAddress, sellerCity, sellerCounty,
      buyerCity, buyerCounty, invoiceNumber, issueDate, dueDate, items,
    };
  }, [buyer, sellerName, sellerCui, sellerAddress, sellerCity, sellerCounty, buyerCity, buyerCounty, invoiceNumber, issueDate, dueDate, items]);

  const savedToDbRef = useRef(false);

  const saveDraftSync = useCallback(() => {
    if (savedToDbRef.current || !user) return;
    const d = formDataRef.current;

    const hasBuyerData = d.buyer.name.trim() !== "" || d.buyer.cui.trim() !== "" || d.buyer.address.trim() !== "";
    const hasBuyerLocation = d.buyerCity.trim() !== "" || d.buyerCounty.trim() !== "";
    const hasInvoiceNum = d.invoiceNumber.trim() !== "";
    const hasDueDateSet = d.dueDate.trim() !== "";
    const hasItemData = d.items.some(i => i.item_name.trim() !== "" || i.unit_price > 0);
    if (!(hasBuyerData || hasBuyerLocation || hasInvoiceNum || hasDueDateSet || hasItemData)) return;

    savedToDbRef.current = true;

    const sub = d.items.reduce((s, i) => s + i.quantity * i.unit_price, 0);
    const vat = d.items.reduce((s, i) => s + i.quantity * i.unit_price * (i.vat_rate / 100), 0);
    const tot = sub + vat;

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

    const storageKey = `sb-${import.meta.env.VITE_SUPABASE_PROJECT_ID}-auth-token`;
    let accessToken = supabaseKey;
    try {
      const session = JSON.parse(localStorage.getItem(storageKey) || "{}");
      if (session?.access_token) accessToken = session.access_token;
    } catch { }

    const headers = {
      "Content-Type": "application/json",
      "apikey": supabaseKey,
      "Authorization": `Bearer ${accessToken}`,
      "Prefer": "return=representation",
    };

    const invoiceBody = JSON.stringify({
      user_id: user.id,
      invoice_number: d.invoiceNumber || null,
      status: "draft",
      seller_name: d.sellerName || null,
      seller_cui: d.sellerCui || null,
      seller_address: d.sellerAddress || null,
      seller_city: d.sellerCity || null,
      seller_county: d.sellerCounty || null,
      buyer_name: d.buyer.name || null,
      buyer_cui: d.buyer.cui || null,
      buyer_address: d.buyer.address || null,
      buyer_city: d.buyerCity || null,
      buyer_county: d.buyerCounty || null,
      subtotal: sub,
      vat_total: vat,
      total: tot,
      issue_date: d.issueDate,
      due_date: d.dueDate || null,
    });

    fetch(`${supabaseUrl}/rest/v1/invoices`, {
      method: "POST",
      headers,
      body: invoiceBody,
      keepalive: true,
    }).then(res => res.json()).then((invoices) => {
      const invoice = Array.isArray(invoices) ? invoices[0] : invoices;
      if (!invoice?.id) return;

      const lineItems = d.items
        .filter(i => i.item_name.trim())
        .map((i, idx) => ({
          invoice_id: invoice.id,
          item_name: i.item_name,
          quantity: i.quantity,
          unit_price: i.unit_price,
          vat_rate: i.vat_rate,
          unit_code: i.unit_code,
          sort_order: idx,
        }));

      if (lineItems.length) {
        fetch(`${supabaseUrl}/rest/v1/invoice_items`, {
          method: "POST",
          headers: { ...headers, "Prefer": "return=minimal" },
          body: JSON.stringify(lineItems),
          keepalive: true,
        });
      }

      try { localStorage.removeItem(AUTOSAVE_KEY); } catch { }
    }).catch(() => { });
  }, [user]);

  useEffect(() => {
    const handleBeforeUnload = () => { saveDraftSync(); };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [saveDraftSync]);

  useEffect(() => {
    return () => {
      if (!savedToDbRef.current) {
        saveDraftSync();
      }
    };
  }, [saveDraftSync]);

  useEffect(() => {
    if (!user || profileLoaded.current) return;
    profileLoaded.current = true;
    supabase
      .from("profiles")
      .select("company_name, cui, company_address, company_city, company_county, default_vat_rate, default_unit_code")
      .eq("user_id", user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          if (!draft) {
            setSellerName(data.company_name || "");
            setSellerCui(data.cui || "");
            setSellerAddress(data.company_address || "");
            setSellerCity(data.company_city || "");
            setSellerCounty(data.company_county || "");
          }
          const defaultVat = Number(data.default_vat_rate) || 0;
          const defaultUnit = data.default_unit_code || "C62";
          setDefaultVatRate(defaultVat);
          setDefaultUnitCode(defaultUnit);
          if (!draft) {
            setItems(prev => prev.map(item => ({ ...item, vat_rate: defaultVat, unit_code: defaultUnit })));
          }
        }
      });
  }, [user]);

  const handleImageSelected = async (file: File) => {
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      toast.error("Selectează o imagine sau un PDF");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fișierul este prea mare (max 10MB)");
      return;
    }

    setScanning(true);
    toast.info("Se scanează factura...", { duration: 10000, id: "scanning" });

    try {
      const base64 = await fileToBase64(file);
      const mimeType = file.type || "image/jpeg";

      const { data, error } = await supabase.functions.invoke("extract-invoice", {
        body: { image_base64: base64, mime_type: mimeType },
      });

      // Backend may reject with 403 + { error: "upgrade_required", message: "..." }
      // when the user is on the Free tier (defense-in-depth alongside UI guard).
      if (error) {
        const ctx: any = (error as any).context;
        const status = ctx?.response?.status ?? ctx?.status;
        let body: any = null;
        try {
          if (typeof ctx?.response?.json === "function") body = await ctx.response.json();
        } catch { /* ignore */ }
        if (status === 403 && body?.error === "upgrade_required") {
          throw new Error(body.message || "Funcție disponibilă doar în planurile plătite.");
        }
        if (status === 429 && body?.error === "rate_limit_exceeded") {
          throw new Error(body.message || "Ai depășit limita de scanări OCR pe oră.");
        }
        throw error;
      }
      if (data?.error === "upgrade_required") throw new Error(data.message || "Funcție disponibilă doar în planurile plătite.");
      if (data?.error === "rate_limit_exceeded") throw new Error(data.message || "Ai depășit limita de scanări OCR pe oră.");
      if (data?.error) throw new Error(data.error);

      if (data.buyer_name || data.buyer_cui || data.buyer_address) {
        setBuyer({
          name: data.buyer_name || "",
          cui: data.buyer_cui || "",
          address: data.buyer_address || "",
        });
      }
      if (data.buyer_city) setBuyerCity(data.buyer_city);
      if (data.buyer_county) setBuyerCounty(data.buyer_county);

      if (data.seller_name) setSellerName(data.seller_name);
      if (data.seller_cui) setSellerCui(data.seller_cui);
      if (data.seller_address) setSellerAddress(data.seller_address);
      if (data.seller_city) setSellerCity(data.seller_city);
      if (data.seller_county) setSellerCounty(data.seller_county);
      if (data.invoice_number) setInvoiceNumber(data.invoice_number);
      if (data.issue_date) setIssueDate(data.issue_date);
      if (data.due_date) setDueDate(data.due_date);

      if (data.items && Array.isArray(data.items) && data.items.length > 0) {
        setItems(
          data.items.map((item: any) => ({
            id: crypto.randomUUID(),
            item_name: item.item_name || "",
            quantity: Number(item.quantity) || 1,
            unit_price: Number(item.unit_price) || 0,
            vat_rate: Number(item.vat_rate) || defaultVatRate,
            unit_code: item.unit_code || defaultUnitCode,
          }))
        );
      }

      toast.dismiss("scanning");
      toast.success("Datele au fost extrase cu succes! Verifică și corectează dacă e nevoie.");
    } catch (err: any) {
      toast.dismiss("scanning");
      toast.error(err.message || "Nu s-au putut extrage datele. Încearcă din nou sau completează manual.");
    } finally {
      setScanning(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const lookupCUI = async () => {
    const cui = buyer.cui.trim();
    if (!cui || cui.replace(/^RO/i, "").length < 2) return;

    setCuiLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("cui-lookup", {
        body: { cui },
      });

      if (error) throw error;
      if (data?.error) {
        toast.error(data.error === "CUI not found" ? "CUI-ul nu a fost găsit" : data.error);
        return;
      }

      setBuyer({
        cui: buyer.cui,
        name: data.name || "",
        address: data.address || "",
      });
      if (data.city) setBuyerCity(data.city);
      if (data.county) setBuyerCounty(data.county);
      setBuyerVatRegistered(data.vat_registered ?? null);
      setBuyerEFactura(data.e_factura ?? null);
      toast.success("Date completate automat de la ANAF!");
    } catch {
      toast.error("Nu s-au putut prelua datele de la ANAF. Completează manual.");
    } finally {
      setCuiLoading(false);
    }
  };

  const addItem = () => {
    setItems([...items, { id: crypto.randomUUID(), item_name: "", quantity: 1, unit_price: 0, vat_rate: defaultVatRate, unit_code: defaultUnitCode }]);
  };

  const removeItem = (id: string) => {
    if (items.length === 1) return;
    setItems(items.filter((i) => i.id !== id));
  };

  const updateItem = (id: string, field: keyof LineItem, value: string | number) => {
    setItems(items.map((i) => (i.id === id ? { ...i, [field]: value } : i)));
  };

  const subtotal = items.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  const vatTotal = items.reduce((s, i) => s + i.quantity * i.unit_price * (i.vat_rate / 100), 0);
  const total = subtotal + vatTotal;

  const handleSave = async (asDraft = true) => {
    if (!user) return;
    if (hasReachedLimit) {
      toast.error(`Ai atins limita de ${limit} facturi luna aceasta. Fă upgrade pentru a continua.`);
      return;
    }
    savedToDbRef.current = true;
    setSaving(true);
    try {
      const { data: invoice, error } = await supabase
        .from("invoices")
        .insert({
          user_id: user.id,
          invoice_number: invoiceNumber || null,
          status: asDraft ? "draft" : "pending",
          seller_name: sellerName || null,
          seller_cui: sellerCui || null,
          seller_address: sellerAddress || null,
          seller_city: sellerCity || null,
          seller_county: sellerCounty || null,
          buyer_name: buyer.name || null,
          buyer_cui: buyer.cui || null,
          buyer_address: buyer.address || null,
          buyer_city: buyerCity || null,
          buyer_county: buyerCounty || null,
          subtotal,
          vat_total: vatTotal,
          total,
          issue_date: issueDate,
          due_date: dueDate || null,
        } as any)
        .select()
        .single();

      if (error) throw error;

      const lineItems = items
        .filter((i) => i.item_name.trim())
        .map((i, idx) => ({
          invoice_id: invoice.id,
          item_name: i.item_name,
          quantity: i.quantity,
          unit_price: i.unit_price,
          vat_rate: i.vat_rate,
          unit_code: i.unit_code,
          sort_order: idx,
        } as any));

      if (lineItems.length) {
        const { error: itemsError } = await supabase.from("invoice_items").insert(lineItems);
        if (itemsError) throw itemsError;
      }

      // Pre-save PDF + XML to storage in background (best-effort, non-blocking)
      // so the "send to accountant" feature can attach the same file the user sees.
      uploadInvoiceFiles({
        id: invoice.id,
        user_id: user.id,
        invoice_number: invoiceNumber || null,
        issue_date: issueDate,
        due_date: dueDate || null,
        seller_name: sellerName || null,
        seller_cui: sellerCui || null,
        seller_address: sellerAddress || null,
        seller_city: sellerCity || null,
        seller_county: sellerCounty || null,
        buyer_name: buyer.name || null,
        buyer_cui: buyer.cui || null,
        buyer_address: buyer.address || null,
        buyer_city: buyerCity || null,
        buyer_county: buyerCounty || null,
        subtotal,
        vat_total: vatTotal,
        total,
        items: items.filter((i) => i.item_name.trim()).map((i) => ({
          item_name: i.item_name,
          quantity: i.quantity,
          unit_price: i.unit_price,
          vat_rate: i.vat_rate,
          unit_code: i.unit_code,
        })),
      }).catch(() => { /* silent */ });

      toast.success(asDraft ? "Factură salvată ca ciornă" : "Factură trimisă!");
      clearDraft();
      navigate("/dashboard");
    } catch (err: any) {
      toast.error(err.message || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || limitLoading) return null;

  if (hasReachedLimit) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="container py-16 max-w-md text-center space-y-6">
          <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="h-8 w-8 text-primary" />
          </div>
          <h2 className="font-heading text-2xl font-bold">Limită Atinsă</h2>
          <p className="text-muted-foreground">
            Ai folosit toate cele <span className="font-semibold text-foreground">{limit} facturi</span> din planul tău.
            Fă upgrade pentru a continua facturarea.
          </p>
          <Button size="lg" className="w-full h-12 font-semibold" onClick={() => navigate("/select-plan")}>
            Fă Upgrade
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")}>
            Înapoi la Dashboard
          </Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <main className="container py-6 max-w-xl space-y-8">
          {remaining <= 3 && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-center">
              Mai ai <span className="font-bold text-primary">{remaining}</span> {remaining === 1 ? "factură gratuită" : "facturi gratuite"} rămase.{" "}
              <button className="underline text-primary font-medium" onClick={() => navigate("/select-plan")}>
                Fă upgrade
              </button>
            </div>
          )}

          <ScanSection
            canScanOCR={canScanOCR}
            scanning={scanning}
            onImageSelected={handleImageSelected}
          />

          <div className="relative flex items-center gap-4">
            <div className="flex-1 border-t border-border" />
            <span className="text-sm text-muted-foreground">sau completează manual</span>
            <div className="flex-1 border-t border-border" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Nr. Factură</Label>
              <Input placeholder="EZF-001" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Data Emiterii</Label>
              <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Scadență</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
          </div>

          <SellerForm
            sellerName={sellerName}
            sellerCui={sellerCui}
            sellerAddress={sellerAddress}
            sellerCity={sellerCity}
            sellerCounty={sellerCounty}
            onSellerNameChange={setSellerName}
            onSellerCuiChange={setSellerCui}
            onSellerAddressChange={setSellerAddress}
            onSellerCityChange={setSellerCity}
            onSellerCountyChange={setSellerCounty}
          />

          <BuyerForm
            buyer={buyer}
            buyerCity={buyerCity}
            buyerCounty={buyerCounty}
            buyerVatRegistered={buyerVatRegistered}
            buyerEFactura={buyerEFactura}
            cuiLoading={cuiLoading}
            onBuyerChange={setBuyer}
            onBuyerCityChange={setBuyerCity}
            onBuyerCountyChange={setBuyerCounty}
            onLookupCUI={lookupCUI}
          />

          <LineItemsEditor
            items={items}
            onAddItem={addItem}
            onRemoveItem={removeItem}
            onUpdateItem={updateItem}
          />

          <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{subtotal.toLocaleString("ro-RO", { style: "currency", currency: "RON" })}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">TVA</span>
              <span>{vatTotal.toLocaleString("ro-RO", { style: "currency", currency: "RON" })}</span>
            </div>
            <div className="border-t border-border pt-2 flex justify-between font-heading font-bold text-lg">
              <span>Total</span>
              <span className="text-primary">{total.toLocaleString("ro-RO", { style: "currency", currency: "RON" })}</span>
            </div>
          </div>

          <div className="flex gap-3 pb-8">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" className="flex-1 h-12" onClick={() => handleSave(true)} disabled={saving}>
                  Salvează Ciornă
                </Button>
              </TooltipTrigger>
              <TooltipContent>Salvează factura ca ciornă pentru editare ulterioară</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button className="flex-1 h-12 font-semibold" onClick={() => setShowAnafWarning(true)} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Trimite la ANAF
                </Button>
              </TooltipTrigger>
              <TooltipContent>Trimite factura către sistemul ANAF SPV</TooltipContent>
            </Tooltip>
          </div>

          <Dialog open={showAnafWarning} onOpenChange={setShowAnafWarning}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-warning" />
                  Mod Simulare
                </DialogTitle>
                <DialogDescription className="text-left space-y-2 pt-2">
                  <p>Integrarea cu ANAF SPV <strong>nu este încă activă</strong>. Factura ta va fi salvată cu statusul „În așteptare", dar <strong>nu va fi trimisă efectiv</strong> către ANAF.</p>
                  <p>Vei fi notificat când trimiterea reală devine disponibilă.</p>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setShowAnafWarning(false)}>Anulează</Button>
                <Button onClick={() => { setShowAnafWarning(false); handleSave(false); }}>
                  Salvează oricum
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
      </main>
      <AppFooter />
    </div>
  );
};

export default NewInvoice;
